import React, { useEffect, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { Dialog, DialogPanel } from '@headlessui/react';
import BaseButton from 'flow/components/ui-components/BaseButton';
import TransferTitleItem from './TransferTitleItem';
import TransferOptionRow from 'flow/components/call-transfer-dialog/TransferOptionRow';
import InfoBox, { InfoBoxType } from 'flow/components/ui-components/InfoBox';
import { RowTabStopContext } from 'flow/components/ui-components/row-tab-stop';
import Icon from 'flow/components/ui-components/Icon';

import { makeGetContactDataForNumber } from 'flow/selectors/contact-selectors';
import { getFocusedActiveCall } from 'flow/selectors/call-selectors';
import { extractName } from 'flow/utils';
import { lookupNumber } from 'flow/actions/contact-actions';
import { addUserPhoneLogEntry } from 'flow/utils/browser-utils';
import {
    getNumberOfOtherPerson,
    prettyFormatE164Number,
} from 'flow/utils/call-utils';
import { getCountryCode } from 'flow/utils/extension-utils';
import useAppContext from 'flow/hooks/useAppContext';
import useContact from 'flow/hooks/useContact';

const BlindTransferRow = ({ initCall }: { initCall: () => void }) => {
    const { i18next } = useAppContext();
    const primaryText = i18next.t('General.Call.Transfer.Alert.Blind');
    const secondaryText = i18next.t(
        'General.Call.Transfer.Alert.Blind.Subtitle',
    );

    return (
        <TransferOptionRow
            initCall={initCall}
            primaryText={primaryText}
            secondaryText={secondaryText}
            transferOptionIcon={<Icon iconKey="transfer_call" />}
            testId={'direct-transfer'}
            componentId={'blind'}
        />
    );
};

const AttendedTransferRow = ({ initCall }: { initCall: () => void }) => {
    const { i18next } = useAppContext();
    const primaryText = i18next.t('General.Call.Transfer.Alert.Attended');
    const secondaryText = i18next.t(
        'General.Call.Transfer.Alert.Attended.Subtitle',
    );

    return (
        <TransferOptionRow
            initCall={initCall}
            primaryText={primaryText}
            secondaryText={secondaryText}
            transferOptionIcon={<Icon iconKey="supervisorAccountIcon" />}
            testId={'attended-transfer'}
            componentId={'attended'}
        />
    );
};

const NewCallRow = ({ initCall }: { initCall: () => void }) => {
    const { i18next } = useAppContext();
    const primaryText = i18next.t('call.new');
    const secondaryText = i18next.t('call.new.info');

    return (
        <TransferOptionRow
            initCall={initCall}
            primaryText={primaryText}
            secondaryText={secondaryText}
            transferOptionIcon={<Icon iconKey="addCallIcon" />}
            testId={'new-call'}
            componentId={'call'}
        />
    );
};

interface Props {
    transferNumber: string;
    transferContact: any;
    handleNewCall?: () => void;
    handleClose: () => void;
    handleTransferBlind: () => void;
    handleTransferAttended: () => void;
    thisExtensionIsUnavailable?: boolean;
}

const CallTransferDialogContent = ({
    transferNumber,
    transferContact,
    handleNewCall,
    handleClose,
    handleTransferBlind,
    handleTransferAttended,
    thisExtensionIsUnavailable,
}: Props) => {
    const { i18next } = useAppContext();
    const { ownContact } = useContact();
    const dispatch = useAppDispatch();

    const activeCall = useAppSelector(getFocusedActiveCall);
    const callerNumber = getNumberOfOtherPerson(activeCall);

    const getContact = useMemo(makeGetContactDataForNumber, []);
    const callerContact = useAppSelector((state) =>
        //@ts-ignore
        getContact(state, callerNumber),
    );

    const transferDescription = transferContact
        ? transferContact.get('description')
        : null;
    const prettyTransferNumber = prettyFormatE164Number(
        transferNumber,
        getCountryCode(ownContact, null),
    );
    // A contact without a description has an empty string there, not null.
    const transferContactInfo =
        transferDescription ||
        extractName(transferContact, prettyTransferNumber);
    const prettyCallerNumber = prettyFormatE164Number(
        callerNumber,
        getCountryCode(ownContact, null),
    );
    const callerContactInfo = extractName(callerContact, prettyCallerNumber);

    useEffect(() => {
        if (callerNumber && !callerContact) {
            addUserPhoneLogEntry(`Doing numberlookup on ${callerNumber}`);
            dispatch(lookupNumber(callerNumber));
        }
    }, [callerNumber, callerContact]);

    return (
        <Dialog
            open={true}
            onClose={handleClose}
            data-test-id={'call-transfer-dialog'}
            className="absolute inset-0 z-50 flex bg-black/50"
            as="div"
        >
            <DialogPanel className="mx-auto w-full max-w-md transform self-center overflow-hidden rounded-sm bg-white/100 p-4 dark:bg-gray-100">
                <TransferTitleItem
                    transferContactInfo={transferContactInfo}
                    callerContactInfo={callerContactInfo}
                />
                {thisExtensionIsUnavailable && (
                    <InfoBox type={InfoBoxType.Warning}>
                        {i18next.t(
                            'General.Call.Transfer.Alert.Extension.Unavailable',
                            { transferInfo: transferContactInfo },
                        )}
                    </InfoBox>
                )}

                <BlindTransferRow initCall={handleTransferBlind} />
                <AttendedTransferRow initCall={handleTransferAttended} />
                {handleNewCall && <NewCallRow initCall={handleNewCall} />}

                <div className="mt-5 flex w-full justify-end">
                    <BaseButton
                        onClick={handleClose}
                        className="hover:darken-20 text-gray-600 filter"
                    >
                        {i18next.t('Cancel')}
                    </BaseButton>
                </div>
            </DialogPanel>
        </Dialog>
    );
};

// A list row can open this dialog. Context passes through portals, so the
// dialog would otherwise take the row's Tab stop state.
const CallTransferDialog = (props: Props) => (
    <RowTabStopContext.Provider value={undefined}>
        <CallTransferDialogContent {...props} />
    </RowTabStopContext.Provider>
);

export default React.memo(CallTransferDialog);
